export const AI_ASSISTANT_API_URL =
   process.env.REACT_APP_AI_ASSISTANT_API_URL ||
   "http://localhost:3001/open-ai/rag";

const STREAM_STATUS_LABELS = {
   searching: "Searching…",
   thinking: "Thinking…",
   generating: "Generating…",
};

export function formatStreamStatus(status) {
   if (!status) return null;
   const key = String(status).trim().toLowerCase();
   if (STREAM_STATUS_LABELS[key]) return STREAM_STATUS_LABELS[key];
   return `${key.charAt(0).toUpperCase()}${key.slice(1)}…`;
}

export function parseMaybeJson(raw) {
   if (!raw) return null;
   if (typeof raw === "object") return raw;
   try {
      return JSON.parse(raw);
   } catch {
      return raw;
   }
}

export function extractAnswer(payload) {
   if (!payload) return "";
   if (typeof payload === "string") return payload;
   return (
      payload.answer ||
      payload.response ||
      payload.message ||
      payload.data?.answer ||
      payload.data?.response ||
      payload.data?.message ||
      ""
   );
}

function extractStreamDelta(payload) {
   if (!payload) return "";
   if (typeof payload === "string") return payload;
   if (payload.done === true) return "";
   return (
      payload.delta ||
      payload.content ||
      payload.text ||
      payload.token ||
      payload.answer ||
      payload.response ||
      payload.message ||
      payload.data?.delta ||
      payload.data?.content ||
      payload.data?.text ||
      payload.data?.answer ||
      payload.choices?.[0]?.delta?.content ||
      payload.choices?.[0]?.message?.content ||
      ""
   );
}

function extractStreamStatus(payload) {
   if (!payload || typeof payload !== "object") return null;
   const status = payload.status;
   if (typeof status !== "string" || !status.trim()) return null;
   if (extractStreamDelta(payload)) return null;
   return status.trim().toLowerCase();
}

function parseStreamPayload(parsed) {
   if (parsed === "DONE" || parsed == null) return null;

   const status = extractStreamStatus(parsed);
   if (status) return { type: "status", status };

   const text = extractStreamDelta(parsed);
   if (text) return { type: "text", text };

   if (typeof parsed === "string" && parsed.trim()) {
      return { type: "text", text: parsed };
   }

   return null;
}

function parseSSELine(line) {
   const trimmed = line.trim();
   if (!trimmed || trimmed.startsWith(":")) return null;
   if (trimmed === "[DONE]" || trimmed === "data: [DONE]") return "DONE";
   const dataPrefix = "data: ";
   const payload = trimmed.startsWith(dataPrefix)
      ? trimmed.slice(dataPrefix.length)
      : trimmed;
   if (payload === "[DONE]") return "DONE";
   return parseMaybeJson(payload);
}

function parseStreamBuffer(buffer) {
   const events = [];

   if (buffer.includes("data:")) {
      const parts = buffer.split("\n\n");
      const remaining = parts.pop() ?? "";
      for (const part of parts) {
         for (const line of part.split("\n")) {
            const parsed = parseSSELine(line);
            const event = parseStreamPayload(parsed);
            if (event) events.push(event);
         }
      }
      return { events, remaining };
   }

   if (buffer.includes("\n")) {
      const lines = buffer.split("\n");
      const remaining = lines.pop() ?? "";
      for (const line of lines) {
         const trimmed = line.trim();
         if (!trimmed || trimmed === "[DONE]") continue;
         const parsed = parseMaybeJson(trimmed);
         const event = parseStreamPayload(parsed);
         if (event) events.push(event);
      }
      return { events, remaining };
   }

   return { events, remaining: buffer };
}

function looksLikeStructuredStream(text) {
   const trimmed = text.trimStart();
   return (
      trimmed.startsWith("data:") ||
      trimmed.startsWith("{") ||
      trimmed.startsWith("[")
   );
}

function flushStreamBuffer(buffer) {
   const trimmed = buffer.trim();
   if (!trimmed || trimmed === "[DONE]") return [];

   if (trimmed.includes("data:")) {
      return parseStreamBuffer(`${trimmed}\n\n`).events;
   }

   if (trimmed.includes("\n")) {
      return parseStreamBuffer(`${trimmed}\n`).events;
   }

   const event = parseStreamPayload(parseMaybeJson(trimmed));
   if (event) return [event];

   return [{ type: "text", text: trimmed }];
}

function emitStreamEvents(events, onEvent) {
   for (const event of events) {
      onEvent(event);
   }
}

export async function consumeStreamingResponse(response, onEvent) {
   const contentType = response.headers.get("content-type") || "";

   if (!response.body || contentType.includes("application/json")) {
      const rawBody = await response.text();
      const parsed = parseMaybeJson(rawBody);
      const status = extractStreamStatus(parsed);
      if (status) {
         onEvent({ type: "status", status });
         return;
      }
      const text = extractAnswer(parsed);
      if (text) onEvent({ type: "text", text });
      return;
   }

   const reader = response.body.getReader();
   const decoder = new TextDecoder();
   let buffer = "";

   while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      if (!chunk) continue;

      buffer += chunk;

      if (!looksLikeStructuredStream(buffer)) {
         onEvent({ type: "text", text: chunk });
         buffer = "";
         continue;
      }

      const { events, remaining } = parseStreamBuffer(buffer);
      buffer = remaining;
      emitStreamEvents(events, onEvent);
   }

   emitStreamEvents(flushStreamBuffer(buffer), onEvent);
}
